import type { AppSettings } from "../services/appSetting.service";

const normalize = (s: string | undefined) => (s || "").trim().toLowerCase();

export function isSettingEnabled(val: string | undefined): boolean {
  const v = normalize(val);
  return v === "true" || v === "1";
}

export function pickAppSettingValue(
  settings: AppSettings[],
  group: string,
  key: string,
  branchId: number,
): string | undefined {
  const groupOk = (s: AppSettings) =>
    normalize(s.setting_group) === normalize(group);
  const keyOk = (s: AppSettings) =>
    normalize(s.setting_key) === normalize(key);
  const candidates = settings.filter((s) => groupOk(s) && keyOk(s));
  const branchMatch = candidates.find((s) => (s.branch_id ?? 0) === branchId);
  if (branchMatch) return branchMatch.setting_value;
  const global = candidates.find((s) => (s.branch_id ?? 0) === 0);
  if (global) return global.setting_value;
  return candidates[0]?.setting_value;
}

export type SalesColumnSettings = {
  showPaymentParticulars: boolean;
  showDiscount: boolean;
  showGstValue: boolean;
  showTaxable: boolean;
  /** GST% column — controlled by the gst_percentage app setting. */
  showGstPercent: boolean;
};

export const DEFAULT_SALES_COLUMN_SETTINGS: SalesColumnSettings = {
  showPaymentParticulars: true,
  showDiscount: true,
  showGstValue: true,
  showTaxable: true,
  showGstPercent: true,
};

export function getSalesColumnSettings(
  settings: AppSettings[],
  branchId: number,
): SalesColumnSettings {
  const pick = (key: string) =>
    pickAppSettingValue(settings, "sales settings", key, branchId);

  const showGstValue = isSettingEnabled(pick("gst value"));
  const showTaxable = isSettingEnabled(pick("taxable"));

  return {
    showPaymentParticulars: isSettingEnabled(pick("payment particulars")),
    showDiscount: isSettingEnabled(pick("discount")),
    showGstValue,
    showTaxable,
    showGstPercent: isSettingEnabled(pick("gst_percentage")),
  };
}
